import { useMemo } from 'react';
import { useAuth } from '../context/AuthContext.js';
import {
  hasPermission,
  canViewClientTab,
  getPermissions,
  getAllPermissions,
  isViewOnly,
  hasAnyRole,
  ROLES,
  ROLE_GROUPS,
} from '../utils/permissions.js';

/**
 * Custom hook for role-based permission management
 * Provides easy access to permission checks in components
 */
export const usePermissions = () => {
  const { user } = useAuth();
  const userRole = user?.role || null;

  /**
   * Memoized permissions for all modules
   */
  const permissions = useMemo(() => {
    if (!userRole) {
      return {
        users: { canView: false, canCreate: false, canEdit: false, canDelete: false, canViewClientTab: false },
        tanks: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        containers: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        roles: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        billing: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        tankCleaning: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        dryBoxSurvey: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        onHire: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        eiReceipt: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        tiReport: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        locations: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        contact: { canView: false, canCreate: false, canEdit: false, canDelete: false },
        testimonials: { canView: false, canCreate: false, canEdit: false, canDelete: false },
      };
    }
    return getAllPermissions(userRole);
  }, [userRole]);

  /**
   * Check permission for a specific module and action
   */
  const checkPermission = (module, action) => {
    return hasPermission(userRole, module, action);
  };

  /**
   * Check if user can view client tab in user management
   */
  const canSeeClientTab = useMemo(() => {
    return canViewClientTab(userRole);
  }, [userRole]);

  /**
   * Check if user has view-only access for a module
   */
  const checkViewOnly = (module) => {
    return isViewOnly(userRole, module);
  };

  /**
   * Check if user has any of the specified roles
   */
  const checkHasAnyRole = (roles) => {
    return hasAnyRole(userRole, roles);
  };

  /**
   * Get permissions for a specific module
   */
  const getModulePermissions = (module) => {
    return getPermissions(userRole, module);
  };

  /**
   * Role checking helpers
   */
  const isSystemAdmin = userRole === ROLES.SYSTEM_ADMIN;
  const isAdmin = userRole === ROLES.ADMIN || userRole === ROLES.SYSTEM_ADMIN;
  const isDocumentationManager = userRole === ROLES.DOCUMENTATION_MANAGER;
  const isSurveyInspector = userRole === ROLES.SURVEY_INSPECTOR;
  const isClient = userRole === ROLES.CLIENT;
  const isAccountant = userRole === ROLES.ACCOUNTANT;

  /**
   * Role group helpers
   */
  const hasFullAccess = hasAnyRole(userRole, ROLE_GROUPS.FULL_ACCESS);
  const isTankManager = hasAnyRole(userRole, ROLE_GROUPS.TANK_MANAGERS);
  const isViewOnlyUser = hasAnyRole(userRole, ROLE_GROUPS.VIEW_ONLY);

  return {
    // User info
    userRole,
    user,
    
    // All permissions object
    permissions,
    
    // Permission check functions
    checkPermission,
    checkViewOnly,
    checkHasAnyRole,
    getModulePermissions,
    
    // Special permissions
    canSeeClientTab,
    
    // Role checks
    isSystemAdmin,
    isAdmin,
    isDocumentationManager,
    isSurveyInspector,
    isClient,
    isAccountant,
    
    // Role group checks
    hasFullAccess,
    isTankManager,
    isViewOnlyUser,
    
    // Constants
    ROLES,
    ROLE_GROUPS,
  };
};

export default usePermissions;
